import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

function ScheduleModal({ event, defaultDate, onClose }) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isEdit = !!event;

  const [formData, setFormData] = useState({
    worker_id: user?.role === 'WORKER' ? user.id : '',
    start_time: defaultDate ? format(defaultDate, "yyyy-MM-dd'T'HH:mm") : '',
    end_time: defaultDate ? format(defaultDate, "yyyy-MM-dd'T'HH:mm") : '',
    title: '',
    description: '',
    job_id: null,
  });

  useEffect(() => {
    if (event) {
      setFormData({
        worker_id: event.worker_id || (user?.role === 'WORKER' ? user.id : ''),
        start_time: event.start_time ? format(parseISO(event.start_time), "yyyy-MM-dd'T'HH:mm") : '',
        end_time: event.end_time ? format(parseISO(event.end_time), "yyyy-MM-dd'T'HH:mm") : '',
        title: event.title || '',
        description: event.description || '',
        job_id: event.job_id || null,
      });
    }
  }, [event, user]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/workers/${data.worker_id}/schedule`, {
        startTime: data.start_time,
        endTime: data.end_time,
        title: data.title,
        description: data.description,
        jobId: data.job_id,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Schedule created successfully');
      queryClient.invalidateQueries(['schedules']);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create schedule');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      // TODO: Implement update endpoint
      toast.error('Update not yet implemented');
    },
    onSuccess: () => {
      toast.success('Schedule updated successfully');
      queryClient.invalidateQueries(['schedules']);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update schedule');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      // TODO: Implement delete endpoint
      toast.error('Delete not yet implemented');
    },
    onSuccess: () => {
      toast.success('Schedule deleted successfully');
      queryClient.invalidateQueries(['schedules']);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete schedule');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Schedule' : 'Add Schedule'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {user?.role !== 'WORKER' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Worker ID *
              </label>
              <input
                type="text"
                name="worker_id"
                required
                value={formData.worker_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Enter worker ID"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="datetime-local"
                name="start_time"
                required
                value={formData.start_time}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <input
                type="datetime-local"
                name="end_time"
                required
                value={formData.end_time}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Enter schedule title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Enter schedule description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Inspection ID (Optional)
            </label>
            <input
              type="number"
              name="job_id"
              value={formData.job_id || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Link to inspection ID"
            />
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            {isEdit && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduleModal;
